import { useEffect, useState } from "react";

/**
 * Track real connectivity. `navigator.onLine` is unreliable (often false
 * positives in preview/iframe environments), so when the browser reports
 * offline we double-check by issuing a tiny no-cors fetch before flipping
 * the state. Only confirmed offline → returns false.
 */
export function useOnlineStatus(): boolean {
  const [isOnline, setIsOnline] = useState<boolean>(true);

  useEffect(() => {
    if (typeof window === "undefined") return;

    let cancelled = false;
    let offlineTimer: number | undefined;

    async function probeConnection(): Promise<boolean> {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 4000);

      try {
        await fetch(`/api/health?online_check=${Date.now()}`, {
          method: "GET",
          cache: "no-store",
          signal: controller.signal,
        });
        return true;
      } catch {
        return false;
      } finally {
        window.clearTimeout(timeout);
      }
    }

    async function verify(targetOnline: boolean) {
      // If browser claims online, trust it immediately.
      if (targetOnline) {
        if (offlineTimer) window.clearTimeout(offlineTimer);
        if (!cancelled) setIsOnline(true);
        return;
      }
      // Browser claims offline — only show the banner after two consecutive
      // failed probes. This avoids false positives in iframes / preview.
      const firstProbeOk = await probeConnection();
      if (cancelled || firstProbeOk) {
        if (!cancelled) setIsOnline(true);
        return;
      }

      if (offlineTimer) window.clearTimeout(offlineTimer);
      offlineTimer = window.setTimeout(() => {
        void probeConnection().then((secondProbeOk) => {
          if (cancelled) return;
          setIsOnline(secondProbeOk);
        });
      }, 1500);
    }

    const handleOnline = () => void verify(true);
    const handleOffline = () => void verify(false);

    // Initial sanity check: if we booted thinking we're offline, verify.
    if (!navigator.onLine) void verify(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      cancelled = true;
      if (offlineTimer) window.clearTimeout(offlineTimer);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return isOnline;
}
